/**
 * @experimental
 * This component duplicates functionality from @components/common/base/popup, but in this case more appropriate for using with news popup, because respects design requirements (like separator line)
 * TODO: Consider to replace it with @components/common/base/popup after adding necessary features to popup component.
 * Also consider moving to the popup component the ability to specify size by a template value, as defined in the design layouts ('small' | 'medium' | 'large'), scaleIn effect, simpler API, avoiding excessive customization
 */
import React, {useCallback, useEffect, useState} from 'react'
import {Close} from '@components/common/icons/icons'
import {MoveToBody} from '@components/move-to-body'
import {noPropagate, useGlobalKeyDown} from '@containers/hooks'
import * as Tw from './modal-tw'

const sizeToPixels = (size: 'small' | 'medium' | 'medium-plus' | 'large') => {
  switch (size) {
    case 'small':
      return '400px'
    case 'medium':
      return '630px'
    case 'medium-plus':
      return '640px'
    case 'large':
      return '800px'
    default:
      return '630px'
  }
}

export interface Props {
  title: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  closeByClickOutside?: boolean
  closeByEsc?: boolean
  closeByX?: boolean
  onClose?: () => void
  showHeader?: boolean
  alwaysShowHeaderSeparator?: boolean
  alwaysShowFooterSeparator?: boolean
  size?: 'small' | 'medium' | 'medium-plus' | 'large'
  /**
   * If true, the size of the modal will be applied by inline styles, otherwise by a class
   */
  useInlineSize?: boolean
  zIndex?: number
  windowClassName?: string
  headerClassName?: string
  titleClassName?: string
  contentClassName?: string
  footerClassName?: string
  coloredFooter?: boolean
}

export const Modal = (props: Props) => {
  const {
    title,
    children,
    footer,
    closeByClickOutside = true,
    closeByEsc = true,
    closeByX = true,
    onClose,

    /* Appearance */
    showHeader = true,
    alwaysShowHeaderSeparator = true,
    alwaysShowFooterSeparator = false,
    size = 'medium',
    zIndex = 2000,
    windowClassName,
    headerClassName,
    titleClassName,
    contentClassName,
    footerClassName,
    coloredFooter
  } = props

  /**
   * We have to use state instead of ref, because component MoveToBody use delayed render and this becomes the reason for post-useEffect ref assignment
   */
  const [contentElement, setContentElement] = useState<HTMLDivElement>()
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false)

  /**
   * Track size to detect scroll. It's necessary to show footer separator
   */
  useEffect(() => {
    const element = contentElement
    if (!element) {
      return undefined
    }

    const checkVerticalScroll = () => {
      const hasScroll = element.scrollHeight > element.clientHeight
      setHasVerticalScroll(hasScroll)
    }

    const resizeObserver = new ResizeObserver(() => {
      checkVerticalScroll()
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [contentElement])

  const closeHandler = useCallback(() => {
    onClose?.()
  }, [props.onClose])

  useGlobalKeyDown(
    closeByEsc
      ? {
          escape: closeHandler
        }
      : {}
  )

  const handleClickOverlay = () => {
    if (closeByClickOutside) {
      closeHandler()
    }
  }

  const handleClickModal = (event: React.MouseEvent) => {
    noPropagate(event)
  }

  return (
    <MoveToBody zIndex={zIndex}>
      <Tw.TwOverlay onClick={handleClickOverlay}>
        <Tw.TwWindow
          onClick={handleClickModal}
          $size={size}
          className={windowClassName}
          style={
            props.useInlineSize
              ? {
                  width: sizeToPixels(size)
                }
              : undefined
          }
        >
          {showHeader && (
            <Tw.TwHeader
              $showSeparator={alwaysShowHeaderSeparator}
              $withXButton={closeByX}
              className={headerClassName}
            >
              <Tw.TwTitle className={titleClassName}>{title}</Tw.TwTitle>
              {closeByX && (
                <Tw.TwXButton onClick={closeHandler}>
                  <Close />
                </Tw.TwXButton>
              )}
            </Tw.TwHeader>
          )}
          <Tw.TwContent
            ref={setContentElement}
            $smallTopPadding={showHeader && !alwaysShowHeaderSeparator}
            $smallBottomPadding={!!footer}
            className={contentClassName}
          >
            {children}
          </Tw.TwContent>
          {footer && (
            <Tw.TwFooter
              className={footerClassName}
              $showSeparator={hasVerticalScroll || alwaysShowFooterSeparator}
              $colored={coloredFooter}
            >
              {footer}
            </Tw.TwFooter>
          )}
        </Tw.TwWindow>
      </Tw.TwOverlay>
    </MoveToBody>
  )
}
